import { useMutation } from "@tanstack/react-query";
import { service, useAuthConfig } from "@theprelude/core";
import {
  Button,
  Form,
  HelpIcon,
  InputFormField,
  notifyError,
} from "@theprelude/ds";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import { DefaultOverlay } from "../default-overlay";
import { ServiceUserModal } from "../modal/service-user-modal";

type ServiceUserForm = {
  name?: string;
};

export const serviceUserSchema = z.tuple([z.literal("service-user")]);

const ServiceUserOverlay = () => {
  const overlay = useOverlay();
  const authConfig = useAuthConfig();
  const [serviceUser, setServiceUser] = useState<{
    token: string;
    name: string;
  } | null>(null);

  const form = useForm<ServiceUserForm>();

  const createServiceUser = useMutation({
    mutationFn: async (data: ServiceUserForm) => {
      return service(authConfig).iam.createServiceUser(data.name);
    },
  });

  const submit = (data: ServiceUserForm) => {
    createServiceUser.mutate(data, {
      onSuccess: (response) => {
        setServiceUser(response);
      },
      onError: (error) => {
        notifyError({
          message: "Failed to create service user",
          dismissible: false,
          error,
        });
      },
    });
  };

  return (
    <DefaultOverlay title={"Add a service user"}>
      <div className="p-4">
        {serviceUser && (
          <ServiceUserModal
            onClose={overlay.close}
            name={serviceUser.name}
            token={serviceUser.token}
          />
        )}

        {!serviceUser && (
          <>
            <div className="mb-4 flex items-center gap-2">
              <HelpIcon className="flex-shrink-0 text-secondary-foreground" />
              <p>
                This user will have limited access and will only be able to add
                new endpoints.{" "}
                <a
                  href="https://support.preludesecurity.com/docs/permissions"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Learn more
                </a>
              </p>
            </div>

            <Form
              {...form}
              onSubmitValid={submit}
              className="flex max-w-md flex-col gap-4"
            >
              <InputFormField
                {...form.register("name")}
                placeholder="(Optional) Name for service user"
                label="Name"
              />

              <Button
                loading={createServiceUser.isPending}
                disabled={createServiceUser.isPending}
                iconPosition="right"
                intent="primary"
                type="submit"
              >
                Create service user
              </Button>
            </Form>
          </>
        )}
      </div>
    </DefaultOverlay>
  );
};

export default ServiceUserOverlay;
